/**
 * Complete Step Component
 * Success screen with transaction details.
 * Restyled with NowRamp theme tokens.
 */

import { useRamp } from '../context/RampContext';
import { useT } from '../i18n';

export interface CompleteStepProps {
  className?: string;
  explorerUrl?: string;
  onDone?: () => void;
  doneButtonText?: string;
}

export function CompleteStep({
  className = '',
  explorerUrl,
  onDone,
  doneButtonText,
}: CompleteStepProps) {
  const { orderStatus, state, dispatch } = useRamp();
  const t = useT();
  const isSell = state.flowType === 'sell';

  const getExplorerUrl = () => {
    if (!orderStatus?.transactionHash) return null;
    if (!explorerUrl) {
      const defaultExplorers: Record<string, string> = {
        ethereum: 'https://etherscan.io/tx/{txHash}',
        polygon: 'https://polygonscan.com/tx/{txHash}',
        arbitrum: 'https://arbiscan.io/tx/{txHash}',
        optimism: 'https://optimistic.etherscan.io/tx/{txHash}',
        base: 'https://basescan.org/tx/{txHash}',
        bitcoin: 'https://mempool.space/tx/{txHash}',
        solana: 'https://solscan.io/tx/{txHash}',
        avalanche: 'https://snowtrace.io/tx/{txHash}',
        fantom: 'https://ftmscan.com/tx/{txHash}',
      };
      const baseUrl = defaultExplorers[state.network] || defaultExplorers.ethereum;
      return baseUrl.replace('{txHash}', orderStatus.transactionHash);
    }
    return explorerUrl.replace('{txHash}', orderStatus.transactionHash);
  };

  const handleDone = () => {
    if (onDone) {
      onDone();
    } else {
      dispatch({ type: 'RESET' });
    }
  };

  const explorerLink = getExplorerUrl();

  return (
    <div className={`flex flex-col items-center px-8 py-12 text-center ${className}`}>
      {/* Success icon */}
      <div
        className="flex items-center justify-center mb-5"
        style={{ width: 64, height: 64, borderRadius: '50%', background: 'var(--t-success, #10b981)' }}
      >
        <svg viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path
            d="M4.83 12 9 16.17 19.59 5.59"
            strokeDasharray="24"
            strokeDashoffset="0"
            style={{ animation: 'checkDraw 0.6s ease-out' }}
          />
        </svg>
      </div>

      <h2 className="text-[20px] font-semibold mb-2" style={{ color: 'var(--t-text)' }}>
        {isSell ? t('complete.sellTitle') : t('complete.buyTitle')}
      </h2>

      <p className="text-[14px] mb-6" style={{ color: 'var(--t-text-secondary)' }}>
        {isSell
          ? t('complete.sellDesc', { fiat: orderStatus?.fiatCurrency || state.fiatCurrency })
          : t('complete.buyDesc', { crypto: orderStatus?.cryptoCurrency || state.cryptoCurrency })
        }
      </p>

      {orderStatus && (
        <div className="w-full text-left mb-6" style={{ background: 'var(--t-surface)', borderRadius: 'var(--t-card-radius, 20px)', padding: 16 }}>
          <DetailRow
            label={isSell ? t('complete.amountSell') : t('complete.amountBuy')}
            value={`${orderStatus.cryptoAmount} ${orderStatus.cryptoCurrency}`}
            highlight
          />
          <DetailRow
            label={isSell ? t('complete.paidSell') : t('complete.paidBuy')}
            value={`${orderStatus.fiatAmount} ${orderStatus.fiatCurrency}`}
          />
          <DetailRow label={t('complete.network')} value={orderStatus.network} />
          <DetailRow
            label={t('complete.wallet')}
            value={`${orderStatus.walletAddress.slice(0, 10)}...${orderStatus.walletAddress.slice(-8)}`}
            mono
          />
          {orderStatus.transactionHash && (
            <DetailRow
              label={t('complete.transaction')}
              value={`${orderStatus.transactionHash.slice(0, 10)}...${orderStatus.transactionHash.slice(-8)}`}
              mono
            />
          )}
          <DetailRow label={t('complete.provider')} value={orderStatus.gateway} />
          <DetailRow label={t('complete.orderId')} value={orderStatus.orderId} mono />
          {orderStatus.completedAt && (
            <DetailRow
              label={t('complete.completed')}
              value={new Date(orderStatus.completedAt).toLocaleString()}
            />
          )}
        </div>
      )}

      <div className="w-full flex flex-col gap-2">
        {explorerLink && (
          <a
            href={explorerLink}
            target="_blank"
            rel="noopener noreferrer"
            className="w-full font-semibold text-[14px] flex items-center justify-center transition-all"
            style={{ height: 48, borderRadius: 'var(--t-cta-radius, 100px)', background: 'var(--t-surface)', color: 'var(--t-text)', textDecoration: 'none' }}
          >
            {t('complete.viewExplorer')}
          </a>
        )}
        <button
          className="w-full font-semibold text-[16px] transition-all"
          style={{ height: 60, borderRadius: 'var(--t-cta-radius, 100px)', background: 'var(--t-cta-bg)', color: 'var(--t-cta-text)' }}
          onClick={handleDone}
        >
          {doneButtonText || t('complete.done')}
        </button>
      </div>
    </div>
  );
}

function DetailRow({ label, value, mono, highlight }: { label: string; value: string; mono?: boolean; highlight?: boolean }) {
  return (
    <div
      className="flex justify-between items-center py-2"
      style={{ borderBottom: '1px solid var(--t-divider)' }}
    >
      <span className="text-[13px]" style={{ color: 'var(--t-text-secondary)' }}>{label}</span>
      <span
        className={`text-[13px] font-medium ${mono ? 'font-mono' : ''}`}
        style={{ color: highlight ? 'var(--t-success, #10b981)' : 'var(--t-text)' }}
      >
        {value}
      </span>
    </div>
  );
}
